import React from "react";
import { useLocation, Link as RouterLink } from "react-router-dom";
import { styled } from "@mui/material/styles";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import NotificationsList from "./notification/NotificationsList";
import AccountMenu from "./AccountMenu";

export interface CustomAppBarProps extends MuiAppBarProps {
  handleDrawerOpen: () => void;
}

const drawerWidth = 240;

const AppBarStyled = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "handleDrawerOpen" && prop !== "open",
})<CustomAppBarProps>(({ theme }) => ({
  backgroundColor: "#ffffff",
  boxShadow: "none",
  zIndex: theme.zIndex.drawer + 1,
  borderBottom: "1px solid #E4E7EC",
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  [theme.breakpoints.up("md")]: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
  },
}));

function useBreadcrumbs() {
  const location = useLocation();
  const pathnames = location.pathname.split("/").filter((x) => x);
  return pathnames;
}

const formatCrumb = (value: string) =>
  value
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const CustomAppBar: React.FC<CustomAppBarProps> = ({
  handleDrawerOpen,
  ...rest
}) => {
  const breadcrumbs = useBreadcrumbs();

  const currentPage =
    breadcrumbs.length > 0
      ? formatCrumb(breadcrumbs[breadcrumbs.length - 1])
      : "Dashboard";

  return (
    <AppBarStyled
      handleDrawerOpen={handleDrawerOpen}
      position="fixed"
      {...rest}
    >
      <Toolbar
        sx={{
          minHeight: { xs: 56, sm: 64 },
          px: { xs: 1, sm: 2 },
          position: "relative",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
          <Box sx={{ display: { xs: "block", md: "none" }, mr: 1 }}>
            <IconButton
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              sx={{
                bgcolor: "background.level1",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                color: "text.primary",
                height: 40,
                width: 40,
                "&:hover": {
                  bgcolor: "background.level2",
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
          <Typography
            color="text.primary"
            sx={{
              display: { xs: "block", sm: "none" },
              fontSize: 16,
              fontWeight: 700,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {currentPage}
          </Typography>
          <Breadcrumbs
            aria-label="breadcrumb"
            sx={{
              display: { xs: "none", sm: "flex" },
              "& .MuiBreadcrumbs-separator": { mx: 1 },
              "& a, & p": { fontSize: 14, fontWeight: 600 },
            }}
          >
            <Link
              component={RouterLink}
              to="/"
              underline="hover"
              color="inherit"
            >
              Dashboard
            </Link>
            {breadcrumbs.map((value, index) => {
              const to = `/${breadcrumbs.slice(0, index + 1).join("/")}`;
              const isLast = index === breadcrumbs.length - 1;
              return isLast ? (
                <Typography key={to} color="text.primary">
                  {formatCrumb(value)}
                </Typography>
              ) : (
                <Link
                  key={to}
                  component={RouterLink}
                  to={to}
                  underline="hover"
                  color="inherit"
                >
                  {formatCrumb(value)}
                </Link>
              );
            })}
          </Breadcrumbs>
        </Box>

        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            flexShrink: 0,
            gap: { xs: 0.25, sm: 0.5 },
          }}
        >
          <NotificationsList />
          <AccountMenu />
        </Box>
      </Toolbar>
    </AppBarStyled>
  );
};

export default CustomAppBar;
